"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, HardHat, Layers, Construction, ArrowRight, CircleCheck as CheckCircle2, FileCheck2, Building2, MapPin } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const services = [{
  icon: ShieldCheck,
  title: "Protective scaffolds and containment",
  description: "Heavy-duty debris netting, monarflex sheeting, and protective catch fans to keep public walkways and surrounding structures secure during structural takedowns."
}, {
  icon: Construction,
  title: "Edge protection and facade retention",
  description: "Engineered edge guarding, internal void protection, and temporary bracing designed to secure external walls while floor plates and roofs are stripped."
}, {
  icon: Layers,
  title: "Phased strip-out access",
  description: "Adaptable scaffolding sequences designed for progressive dismantling, allowing demolition teams safe multi-level access as floors are systematically removed."
}, {
  icon: HardHat,
  title: "Rubbish chutes and loading gantries",
  description: "High-capacity external loading platforms, heavy-duty material run-offs, and secure waste chute runs configured for rapid, contained site clearance."
}];
const accreditations = [{
  name: "NASC Full Member",
  detail: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS Accredited",
  detail: "Contractors Health and Safety Assessment"
}, {
  name: "Constructionline Gold",
  detail: "Pre-qualified supply chain compliance"
}];
const coverageAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "London", "South East"];
export default function DemolitionSupport() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="demolitionscaffoldingsupport" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden text-foreground">
      {/* Decorative subtle background accents */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-muted blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Top Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <Building2 className="w-3.5 h-3.5 text-primary-foreground" />
            <span>Specialist contractor support</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Scaffolding and access support for demolition contractors
          </h1>

          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            BMF Scaffolding Ltd supports demolition contractors across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East with NASC- and CHAS-compliant access solutions.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="shadow-md">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/HealthSafetyAccreditationsPage">
                View safety standards
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Feature Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {services.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-200">
                  <CardHeader className="pb-3">
                    <div className="w-12 h-12 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground leading-relaxed text-sm">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Split Content Box: Demolition Workflow & Accreditations */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Main Informational Panel */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 bg-secondary text-secondary-foreground rounded-2xl p-8 sm:p-10 shadow-lg flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary-foreground/90 text-sm font-medium mb-3">
                <FileCheck2 className="w-4 h-4" />
                <span>Engineered for complex site constraints</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold font-default mb-4">
                Controlled, progressive dismantling support
              </h2>
              <p className="text-secondary-foreground/80 leading-relaxed mb-6">
                Demolition projects require dynamic scaffolding that evolves as the structure comes down. We coordinate closely with your structural engineers and site supervisors to plan safe strike sequences, load allowances, and progressive handovers.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {["TG20:21 compliant design calculations", "Weekly statutory scaffold inspections", "RAMS tailored to demolition hazards", "Direct owner-led project accountability"].map((point, i) => <div key={i} data-index={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-primary-foreground shrink-0 mt-1" />
                    <span className="text-sm text-secondary-foreground/90 font-medium">
                      {point}
                    </span>
                  </div>)}
              </div>
            </div>

            <div className="pt-6 border-t border-secondary-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-xs text-secondary-foreground/70 uppercase tracking-wider font-semibold">
                  Coverage
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {coverageAreas.map((area, idx) => <span key={area} data-index={idx} className="inline-flex items-center gap-1 text-xs text-secondary-foreground/90">
                      <MapPin className="w-3 h-3 text-primary-foreground/70" />
                      {area}
                      {idx < coverageAreas.length - 1 && <span className="text-secondary-foreground/40 ml-1">•</span>}
                    </span>)}
                </div>
              </div>
              <Button asChild variant="default" size="sm" className="shrink-0">
                <Link to="/ContactRequestQuotePage">
                  Request site visit
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Accreditations & Safety Panel */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between bg-card border border-border rounded-2xl p-8 sm:p-10 shadow-sm">
            <div>
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="text-xl font-bold text-foreground font-default">
                  Safety and standards
                </h3>
                <Badge variant="outline" className="border-border">
                  Fully verified
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                All scaffolding installations for demolition scopes adhere strictly to current industry standards and pre-qualification health and safety requirements.
              </p>

              <div className="space-y-4 mb-6">
                {accreditations.map((acc, index) => <div key={acc.name} data-index={index} className="p-4 rounded-xl bg-muted/50 border border-border/60 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center shrink-0 mt-0.5">
                      <ShieldCheck className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground font-default">
                        {acc.name}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {acc.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <Link to="/HealthSafetyAccreditationsPage" className="inline-flex items-center text-sm font-semibold text-foreground hover:text-foreground/80 transition-colors">
                <span>Read our full accreditation and safety details</span>
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}